"use client";

import FrameConfig from "@lib/classes/FrameConfig";
import FrameConfigContext from "@lib/contexts/frame_config";
import useContent from "@lib/use_content";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";

// Pages that should not have frame config updated from URL params.
// This prevents issues like service area being overwritten when returning
// from external payment pages where the parent frame passes different params.
const EXCLUDE_URL_CONFIG_PATHS = [
  "/book/booking",
  "/book/checkout",
  "/frp/booking",
  "/frp/checkout",
];

export default function FrameConfigProvider({ children }: { children: React.ReactNode }) {
  const { data, loading } = useContent(["beErrGroupLogin"]);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [errorDebugMessage, setErrorDebugMessage] = useState<string>("");
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const [loadingConfig, setLoadingConfig] = useState(true);

  // If page was reloaded, was there an error message?
  useEffect(() => {
    if (searchParams?.get("fCError")) setErrorMessage(searchParams?.get("fCError") as string);
    if (searchParams?.get("fCDebug")) setErrorDebugMessage(searchParams?.get("fCDebug") as string);
  }, []);

  // Grab frame config from the URL.
  useEffect(() => {
    if (!data) return;

    // Skip URL processing entirely for excluded paths
    if (EXCLUDE_URL_CONFIG_PATHS.some((p) => pathname?.includes(p))) {
      setLoadingConfig(false);
      return;
    }

    setLoadingConfig(true);
    async function updateFrameConfig() {
      const fc = new FrameConfig();
      let action = await fc.updateFromURL(Object.fromEntries(searchParams?.entries()) || {});
      let adtlQuery = (pathname?.includes("?") ? "&" : "?") + "fcu=true";
      // Preserve widget parameter if present
      if (searchParams?.get("widget")) {
        adtlQuery += `&widget=${searchParams.get("widget")}`;
      }
      if (action?.error) {
        setErrorMessage(data?.content.beErrGroupLogin);
        setErrorDebugMessage(action.error);
        adtlQuery += `&fCError=${data?.content.beErrGroupLogin}&fCDebug=${action.error}`;
      } else if (action?.redirectTo) {
        await router.replace(`/${window.siteId}/${action.redirectTo}`);
        return;
      }

      // If a group was loaded we'll want to reset the server-based props
      if (searchParams?.get("code")) await router.replace(pathname + adtlQuery);

      setLoadingConfig(false);
    }
    if (!searchParams?.get("fcu")) {
      updateFrameConfig();
    } else {
      setLoadingConfig(false);
    }
  }, [loading, searchParams]);

  return (
    <FrameConfigContext.Provider
      value={{ loading: loadingConfig, error: errorMessage, errorDebug: errorDebugMessage }}
    >
      {children}
    </FrameConfigContext.Provider>
  );
}
